import { Box } from "@mui/material";
import React, { useEffect, useMemo, useRef } from "react";

import { useIncident } from "../../hooks/queries/useIncident";
import type { EntityDetailProps } from "../../types/entityRegistry";
import type { MainEntity } from "../../types/entityTypes";
import DetailSections from "../Detail/DetailSections";
import { EntityNavigationProvider } from "../Detail/EntityLink";
import { SourceJumpProvider } from "../Detail/sourceJump";
import EntityFunctionalHeader from "../Layout/EntityDetailOverlay/EntityFunctionalHeader";
import { EntityDetailStatus } from "../Layout/EntityDetailStatus";
import IncidentHead, { UNNAMED_INCIDENT } from "./IncidentHead";
import { incidentSections } from "./incidentSections";

// Width (px) the view keeps to on a page of its own
const STANDALONE_WIDTH = 1200;

/**
 * The detail of an incident: the registry's Detail component of the type.
 *
 * It fetches the incident by its id and shows the overlay's header, the
 * head, and the sections under a strip that stays in view. Loading, not
 * found and graph unavailable show where the existing details show them.
 */
const IncidentDetailView: React.FC<EntityDetailProps> = ({
  entityId,
  mode = "overlay",
  onEntityClick,
  onClose,
  scrollContainerRef,
}) => {
  const { incident, loading, error } = useIncident(entityId || null);

  const ownScrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollRef = scrollContainerRef ?? ownScrollContainerRef;

  const sections = useMemo(
    () => (incident ? incidentSections(incident, mode) : []),
    [incident, mode],
  );

  // What the header reads: it shows the type and opens the full page
  const mainEntity = useMemo<MainEntity | null>(
    () =>
      incident
        ? {
            id: incident.id,
            name: incident.name ?? UNNAMED_INCIDENT,
            entityType: "incident",
          }
        : null,
    [incident],
  );

  // Another incident starts at its top
  const incidentId = incident?.id;
  useEffect(() => {
    if (incidentId && scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [incidentId, scrollRef]);

  if (loading || error) {
    return <EntityDetailStatus loading={loading} error={error} mode={mode} />;
  }

  if (!incident || !mainEntity) {
    return null;
  }

  return (
    <EntityNavigationProvider onEntityClick={onEntityClick}>
      <SourceJumpProvider>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <EntityFunctionalHeader
            mainEntity={mainEntity}
            onClose={onClose || (() => {})}
            mode={mode}
          />
          <Box ref={scrollRef} sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
            <Box
              sx={{
                maxWidth: mode === "standalone" ? STANDALONE_WIDTH : "100%",
                mx: "auto",
              }}
            >
              <IncidentHead incident={incident} />
              <DetailSections
                sections={sections}
                scrollContainerRef={scrollRef}
              />
            </Box>
          </Box>
        </Box>
      </SourceJumpProvider>
    </EntityNavigationProvider>
  );
};

export default IncidentDetailView;
